import { memo, useState } from 'react';
import { Container, Header, SpaceBetween } from '@amzn/awsui-components-core';

import SectionEditorControl from '../v2-common/SectionEditorControl';
import styles from './GuideEditor.module.scss';
import { useGuideEditorViewModel } from './GuideEditorViewModel';
import AddSectionButton from './AddSectionButton';
import EditPageButton from './EditPageButton';
import DeletePageButton from './DeletePageButton';
import EditSectionButton from './EditSectionButton';
import DeleteSectionButton from './DeleteSectionButton';
import NextPageButton from './NextPageButton';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';

/** Editor surface for Guide content. Renders the current page's body and sections. */
const GuideEditor = memo(function GuideEditor() {
  const vm = useGuideEditorViewModel();
  const entityConverter = useEntityConverter();

  // React keys by section.id, so only the newly-added control mounts and reads this.
  const [pendingFocusSectionId, setPendingFocusSectionId] = useState<string | undefined>(undefined);

  if (!vm) return null;

  const pageTitleText = entityConverter(vm.pageTitle);

  return (
    <SpaceBetween size="l">
      <Container>
        <SpaceBetween size="l">
          <div className={styles.headerRow}>
            <Header variant="h3">{pageTitleText}</Header>
            {vm.isModifyPageButtonsVisible && (
              <div className={styles.headerActions}>
                <EditPageButton currentPage={vm.currentPage} />
                <DeletePageButton currentPage={vm.currentPage} pageTitle={pageTitleText} />
              </div>
            )}
          </div>
          <SectionEditorControl
            key={`${vm.currentPage.id}-body`}
            node={vm.currentPage}
            contentId={vm.contentId}
            title={vm.pageTitle}
            isTitleHidden
          />
          {vm.sections.map((section) => (
            <SectionEditorControl
              key={section.id}
              node={section}
              contentId={vm.contentId}
              title={section.title}
              isAutoFocused={section.id === pendingFocusSectionId}
              headerActions={
                <>
                  <EditSectionButton currentPage={vm.currentPage} currentSection={section} />
                  <DeleteSectionButton
                    currentPage={vm.currentPage}
                    currentSection={section}
                    sectionTitle={entityConverter(section.title)}
                  />
                </>
              }
            />
          ))}
          <AddSectionButton
            currentPage={vm.currentPage}
            onSectionAdded={setPendingFocusSectionId}
          />
        </SpaceBetween>
      </Container>
      <div className={styles.nextPageButtonRow}>
        <NextPageButton nextPage={vm.nextPage} />
      </div>
    </SpaceBetween>
  );
});

export default GuideEditor;
